import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'

import { trackTileSliderInteraction } from '@Analytics/tileInteractions'
import AngleLeftSVG from '@Assets/SVG/AngleLeft'
import AngleRightSVG from '@Assets/SVG/AngleRight'
import { ImageV3 } from '@Components/Image'

import styles from './AssetsSlider.module.scss'

// shared by every tile image spot; any preload image must use the exact
// same value so the browser picks (and caches) the same srcset candidate
export const TILE_IMAGE_SIZES = '(max-width: 767px) 50vw, (max-width: 1199px) 33vw, (max-width: 1999px) 25vw, 20vw'

interface AssetsSliderProps {
  assetURLs: string[]
  // extra class for the scroller root — the tiles pass their own module's
  // hook here so tile-scoped sibling selectors (the out-of-stock grayscale)
  // can still reach the slider's images
  className?: string
  // a grouped-tile swap changes the displayed product in place; the id
  // change is what rewinds the slider (static tiles never trigger it)
  id: string
  onFirstImageLoad?: () => void
  priority?: boolean
  // extra class per slide, for tile-specific slide styling (the grouped
  // tile drives the slide padding by container width instead of viewport)
  slideClassName?: string
  tileIndex?: number
  title: string
}

export const AssetsSlider = ({
  assetURLs,
  className,
  id,
  onFirstImageLoad,
  priority = true,
  slideClassName,
  tileIndex = 0,
  title,
}: AssetsSliderProps) => {
  const assetsRef = useRef<HTMLDivElement>(null)
  const [interacted, setInteracted] = useState(false)
  const [activeSlide, setActiveSlide] = useState(0)
  const previousID = useRef(id)
  // set while the swap effect rewinds the slider programmatically: that
  // rewind fires onScroll, which must not count as user interaction
  const suppressScroll = useRef(false)

  const scrollToSlide = (offset: number) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const container = assetsRef.current
    if (container) {
      container.scrollBy({ behavior: 'smooth', left: offset * container.clientWidth })
    }
  }

  // a grouped-tile swap changes the displayed product in place (no remount:
  // that would re-enter the slider arrows at opacity 0 and replay their
  // hover fade-in on every selection) — so rewind the slider state manually.
  // The previousID guard keeps the initial mount from counting as a swap.
  useEffect(() => {
    if (previousID.current === id) {
      return
    }

    previousID.current = id
    setActiveSlide(0)
    // interacted deliberately survives the swap: it's the user's knowledge
    // of THIS tile's slider, not a property of the displayed product — and
    // clearing it would lift the .interacted animation cancel, restarting
    // the scroll hint on the persistent slides
    // only flag when a scroll event will actually fire (scrollLeft changes),
    // or the next genuine user scroll would be swallowed
    if (assetsRef.current && assetsRef.current.scrollLeft !== 0) {
      suppressScroll.current = true
      assetsRef.current.scrollLeft = 0
    }
  }, [id])

  return (
    <>
      <div
        className={clsx(styles.assets, interacted && styles.interacted, className)}
        onScroll={(event) => {
          if (suppressScroll.current) {
            suppressScroll.current = false
            return
          }

          setInteracted(true)
          const { clientWidth, scrollLeft } = event.currentTarget
          if (!clientWidth) {
            return
          }
          const slide = Math.round(scrollLeft / clientWidth)
          if (slide !== activeSlide) {
            // counted once per slide change regardless of input (swipe,
            // trackpad or arrows); folded into page_exited. Only list tiles
            // carry a 1-based tileIndex — callers without one (e.g. the
            // recommendation grids) must not pollute the aggregates with
            // index-0 events
            if (tileIndex) {
              trackTileSliderInteraction(tileIndex)
            }
            setActiveSlide(slide)
          }
        }}
        ref={assetsRef}
      >
        <div className={clsx(styles.slide, slideClassName)}>
          <ImageV3
            alt={title}
            className={styles.image}
            decoding="async"
            draggable={false}
            fetchPriority={priority ? 'high' : undefined}
            fill
            loading={priority ? 'eager' : 'lazy'}
            onLoad={onFirstImageLoad}
            priority={priority}
            sizes={TILE_IMAGE_SIZES}
            src={assetURLs[0]}
            style={{
              objectFit: 'contain',
            }}
            title={title}
          />
        </div>

        {assetURLs.slice(1).map((asset, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className={clsx(styles.slide, slideClassName)}>
            {/* the second slide always renders (the scroll hint reveals it);
                the rest only load once the slider has been interacted with —
                browsers prefetch lazy images in scrollers too eagerly to
                rely on loading="lazy" alone.
                keyed by POSITION, not asset url: a grouped swap then reuses
                the slide elements (only src changes), so the first-view
                scroll-hint animation doesn't restart on freshly-mounted
                slides — it replays only via the wrapper's in-view attribute
                toggle (scroll out and back) or a real remount.
                no .image class: fill's position: absolute must survive so the
                image escapes the slide padding and renders to the edge.
                alt is empty on purpose: inside the tile link these are
                decorative repeats of the product the link already names */}
            {(index === 0 || interacted) && (
              <ImageV3
                alt=""
                decoding="async"
                draggable={false}
                fetchPriority="low"
                fill
                loading="lazy"
                overrideQuality="50,65"
                sizes={TILE_IMAGE_SIZES}
                src={asset}
                style={{
                  objectFit: 'cover',
                }}

              />
            )}
          </div>
        ))}
      </div>

      <div className={styles.dots}>
        {assetURLs.map((asset, index) => (
          <span key={asset} className={clsx(styles.dot, index === activeSlide && styles.activeDot)} />
        ))}
      </div>

      <button
        aria-label="Previous"
        className={clsx(styles.sliderArrow, styles.left)}
        disabled={activeSlide === 0}
        onClick={scrollToSlide(-1)}
        tabIndex={-1}
        type="button"
      >
        <AngleLeftSVG />
      </button>

      <button
        aria-label="Next"
        className={clsx(styles.sliderArrow, styles.right)}
        disabled={activeSlide >= assetURLs.length - 1}
        onClick={scrollToSlide(1)}
        tabIndex={-1}
        type="button"
      >
        <AngleRightSVG />
      </button>
    </>
  )
}
